/**************************************************************************************************
 *
 *    Application configuration used on client
 *    Merges with contents of config-shared.ts (and exports it)
 *
 *    Examples:
 *        Namespaces on global window object to assign various values & libs to
 *
 *        Disease IDs
 *
 *        Default form values:
 *            Development login data (default data for quick developer logins)
 *
 *        Routing/links/URLs:
 *            URLs for sync server
 *            External links, to other parts of CANImmunize website
 *            In-app routes: e.g. `/main/${langUrl}/home`
 *
 *        UI config:
 *            DOM element IDs that are accessed from multiple locations
 *            *   e.g. any that are set via initialFocus prop of CIModal
 *            Animation config (animation length, load spinner auto-timeout, etc)
 */

/******************************************** IMPORTS *********************************************/
import {isDevelopment, isUAT, isQA, releaseEnvShort, isProduction} from 'env-var-helpers';
import {configShared} from './config-shared';
import {getLangFromURL} from 'mad-utils/lib/shared';

declare function require(name: string);
const deepMergeModule = require('deepmerge');

type DeepMergeType = <O1 extends object>(
    o1: object,
    o2: object,
    config?: {clone?: boolean; arrayMerge?: boolean; isMergeableObject?: boolean}
) => O1;

const deepMergeFn: DeepMergeType =
    typeof deepMergeModule === `function`
        ? deepMergeModule
        : deepMergeModule && deepMergeModule.default;

/******************************************** HELPERS *********************************************/
/* URL helpers */
const protocol = `https`;
const hostname = `canimmunize.ca`;
const langUrl = getLangFromURL();

/** Determine the sync-server domain & API subdomain */
const envSubdomainExt = releaseEnvShort === `prod` ? `` : releaseEnvShort + `.`;

const syncDomain = `${protocol}://sync.${envSubdomainExt}${hostname}`;
const syncAPI = `${syncDomain}/api/v2`;
// const syncAPI = `http://localhost:8082/api/v2`;
const syncAPIProd = `${protocol}://sync.${hostname}/api/v2`;
const settingsDomain = syncAPI + `/settings`;

const adminApiBaseUrl = `${protocol}://admin.${envSubdomainExt}${hostname}/api/v2`;

/**** Default input values ****/
// Determine whether to show defaults in general (based on current environment)
const doShowDefaults = isDevelopment && !isUAT && !isQA;

const defaultFullName = doShowDefaults
    ? (process && process.env && process.env.DEV_FULLNAME) || `Meeka Faulkner`
    : ``;

const defaultUsername = doShowDefaults
    ? (process && process.env && process.env.DEV_USERNAME) || `canimmunizetest@gmail.com`
    : ``;

const defaultPassword = doShowDefaults
    ? (process && process.env && process.env.DEV_PASSWORD) || `10qpalwsolDK!`
    : ``;

/***************************************** CLIENT CONFIG ******************************************/
const _configClient = {
    syncAPI,
    /**
     * TODO Reorganize section to contain actual "app data", possibly remove section
     */
    appData: {
        defaultVals: {
            /**
             * TODOs: Confirm still needed; rename (current name is misleading); make i18n-friendly
             */
            spokenLangAbbrev: `English`,
        },

        /**
         * Data used in login & signup processes in development (but not QA or
         * UAT), for convenience
         */
        loginData: {
            devName: defaultFullName,
            devEmail: defaultUsername,
            devPassword: defaultPassword,
        },
    },

    /**
     * Data for dealing with Carrot API
     */
    carrot: {
        buildApiUrl: (carrotQueryParam: string) =>
            isProduction
                ? `https://partner.carrotrewards.ca/action/${carrotQueryParam}/complete`
                : `https://staging-partner.carrotrewards.ca/action/${carrotQueryParam}/complete`,
        authToken: isProduction
            ? `eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJlbnYiOiJwcm9kdWN0aW9uIiwicGFydG5lciI6ImNhbmltbXVuaXplIiwiaWF0IjoxNTM5ODg3MDI0fQ.x4DSV5yjWKxrb-TEZGY1IzHwfAWWDSlE5YKHJnOf4Pw`
            : `eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJlbnYiOiJzdGFnaW5nIiwicGFydG5lciI6ImNhbmltbXVuaXplIiwiaWF0IjoxNTM5ODg3MDE0fQ.-lip2VYkLJxdZs5HDHSv2BhKqv_t9CSYaPOXAGSMKaI`,
    },

    dataConfig: {
        /**
         * TODO do this with Javelin instead
         */
        childConsentRequiredAge: 16,
    },

    /**
     * Cross-project config for animations (animation timings in ms)
     */
    animations: {
        modalHideLengthMs: 300,
        loadSpinnerTimeout: 30000,
        toastDisplayTimeMs: 30,
    },

    /**
     * Routes for specific pages
     *
     * Only handles routes that are in-app and not linked to API calls
     * Used by React Router
     */
    routes: {
        /**
         * Build relative My Records content page route for given [recordID]
         * (if any)
         */
        myRecords: (recordID?: string) => {
            const queryParams = recordID ? `?current_record=${recordID}` : ``;
            return `/main/${langUrl}/my-records` + queryParams;
        },

        forgotPassword: `/auth/${langUrl}/forgot-password?from-login=true`,

        /**
         * Relative path to post-login home screen (for webclient, not
         * CANImmunize website)
         */
        home: `/main/${langUrl}/home`,

        /**
         * Relative path for login screen (pre-login)
         */
        login: `/auth/${langUrl}/login`,

        /**
         * Path to redirect to if route doesn't match any in signup flow
         * (defined in signup-flow-config)
         */
        signupRedirectDefault: `/auth/${langUrl}/signup/create-account`,

        /**
         * Settings routes
         */
        settings: {
            account: `/main/${langUrl}/settings/account`,
            notifications: `/main/${langUrl}/settings/notifications`,
            language: `/main/${langUrl}/settings/language`,
            /**
             * Build URL for route showing settings for record with id [recordID]
             */
            record: (recordId: string) =>
                `/main/${langUrl}/record-settings?current_record=${recordId}`,
        },
    },

    /**
     * DOM element IDs (especially those accessed from multiple locations)
     * Any ID used by CIModal->initialFocus prop should go here
     */
    elementIds: {
        /**
         * Element IDs for pre-login DOM elements
         */
        auth: {
            forgotPasswordBackToLoginLink: `forgot-password-card-login-link`,
            footer: {
                privacyPolicyLink: `prelogin-footer-privacy-policy-link`,
                contactSupportLink: `prelogin-footer-contact-support-link`,
                changeLanguageButton: `prelogin-footer-language-button`,
            },
            login: {
                emailInput: `login-email-input`,
                passwordInput: `login-password-input`,
                submitButton: `login-submit-button`,
                signupLink: `login-signup-link`,
                forgotYourPasswordLink: `login-forgot-your-password-link`,
                resendUnlockEmailButton: `login-resend-unlock-email-button`,
                subheading: `login-card-subheading`,
            },
            signup: {
                nameInput: `signup-name-input`,
                emailInput: `signup-email-input`,
                passwordInput: `signup-password-input`,
                confirmPasswordInput: `signup-confirm-password-input`,
                haveAnAccountLink: `signup-have-an-account-login-link`,
                nextButton: `signup-next-or-submit-button`,
                backButton: `signup-back-button`,
                countryInput: `signup-country-dropdown-input`,
                locationPostalCodeInput: `signup-location-postal-code-input`,
                locationCityInput: `signup-location-signup-city-search`,
            },
        },

        /**
         * Element IDs for post-login DOM elements
         */
        main: {
            cityInputField: `set-city-input-field`,
            canimmunizeLogoHomeLink: `canimmunize-logo-home-link`,
            confirmModalCancelButton: `confirm-modal-cancel-button`,
            dismissAutoDisabledPHUBtn: `dismiss-auto-disabled-phu-button`,
            dismissEnablePHUSuccessModalBtn: `dismiss-enable-phu-success-modal-button`,
            cancelDisablingPhuButton: `cancel-disabling-phu-button`,
            topbarMyRecordsLink: `topbar-my-records-link`,
            topbarAboutDropdown: `topbar-about-dropdown`,
            myRecordsMenuCreateNewRecordButton: `my-records-menu-create-new-record-button`,
            myRecordsPHUStatusBoxLink: `my-records-phu-status-box-settings-link`,
            doseDetail: {
                productInfoInput: `ddv-product-info-field`,
            },
            topbar: {
                myRecordsDropdownButton: `topbar-my-records-dropdown-button`,
                notificationsDropdownButton: `topbar-notifications-dropdown-button`,
                helpDropdownButton: `topbar-help-dropdown-button`,
                myAccountDropdownButton: `topbar-my-account-dropdown-button`,
                menuDrawerToggleButton: `topbar-menu-drawer-toggle-button`,
            },
            myRecordsMenu: {
                questionAboutImmunizationLink: `question-about-immunization-my-records-menu-link`,
            },
            addNewVaccinationModal: {
                searchInput: `add-new-vaccination-modal-search-input`,
            },
            createNewRecord: {
                modalWrapper: `cnr-modal-wrapper`,
                firstNameInput: `cnr-first-name-input`,
                lastNameInput: `cnr-last-name-input`,
                birthdateInput: `cnr-birthdate-input`,
                sexInputBtnFemale: `cnr-sex-input-button-female`,
                sexInputBtnMale: `cnr-sex-input-button-male`,
                consentCheckbox: `cnr-consent-checkbox`,
                nextButton: `cnr-next-button`,
                backButton: `cnr-back-button`,
                cancelButton: `cnr-cancel-button`,
            },
            myRecords: {
                statusTabs: {
                    disease: `status-tab-by-disease`,
                    vaccination: `status-tab-by-vaccination`,
                },
                headerSettingsLink: `my-records-description-header-settings-link`,
                diseaseTabContent: `immunization-status-by-disease-tab-content`,
                immunizationStatusTitle: `my-records-immunization-status-block-title`,
            },
            accountSettings: {
                nameModalInput: `name-account-setting-field`,
                currentPasswordInput: `password-account-setting-field`,
                newPasswordInput: `password-account-setting-field--new-password`,
                confirmPasswordInput: `password-account-setting-field--confirm-password`,
                deleteAccountModalEmailInput: `account-setting-delete-account-modal-email-input`,
                deleteAccountModalMessageInput: `delete-account-setting-field--message`,
            },
            recordSettings: {
                headerRecordLink: `record-settings-header-record-link`,
                deleteModalFirstNameInput: `delete-record-setting-field--first-name`,
                firstNameModalInput: `first-name-record-setting-field`,
                lastNameModalInput: `last-name-record-setting-field`,
                openFirstNameModalButton: `record-setting-open-first-name-modal-button`,
                openLastNameModalButton: `record-setting-open-last-name-modal-button`,
                openRelationshipModalButton: `record-setting-open-relationship-modal-button`,
                openSexModalButton: `record-setting-open-sex-modal-button`,
                openBirthdateModalButton: `record-setting-open-birthdate-modal-button`,
                openRegionModalButton: `record-setting-open-region-modal-button`,
                openCityModalButton: `record-setting-open-city-modal-button`,
                openDoctorModalButton: `record-setting-open-doctor-modal-button`,
                openNotesModalButton: `record-setting-open-notes-modal-button`,
            },
            eligiblePHUSettings: {
                disableButton: `eligible-phu-settings-disable-button`,
                firstNameInput: `eligible-phu-settings-first-name-input`,
                lastNameInput: `eligible-phu-settings-last-name-input`,
                ohipInput: `eligible-phu-settings-ohip-input`,
                phoneInput: `eligible-phu-settings-phone-input`,
                address1Input: `eligible-phu-settings-address1-input`,
                address2Input: `eligible-phu-settings-address2-input`,
                postalCodeInput: `eligible-phu-settings-postal-code-input`,
            },
        },

        /**
         * Element ID for focus point of hidden "Skip to content" link (for
         * accessibility)
         */
        myRecordsMenuStart: {
            normal: `skip-to-my-records-menu-focus-point-normal`,
            small: `skip-to-my-records-menu-focus-point-small`,
        },
        mainContentStart: `skip-to-main-content-focus-point`,
    },

    /**
     * Data/API sources & external links
     */
    urls: {
        /**
         * External links to other CANImmunize website locations
         */
        canimmunizeWeb: {
            /**
             * URL for CANImmunize static home page
             */
            home: `${protocol}://www.${hostname}/${langUrl}/home`,

            /**
             * Privacy policy URL
             */
            privacyPolicy: `${protocol}://www.${hostname}/${langUrl}/privacy-policy`,

            /**
             * Cookie policy URL
             */
            cookiePolicy: `${protocol}://www.${hostname}/${langUrl}/privacy-policy?tab=pp-cookie`,

            /**
             * Consent form URL
             */
            consentStatement: (queryString?: string) => {
                const baseURL =
                    releaseEnvShort === `prod`
                        ? `${protocol}://www.${hostname}/${langUrl}/consent-statement`
                        : `${protocol}://www.${releaseEnvShort}.${hostname}/${langUrl}/consent-statement`;

                const urlWithQuery = queryString ? `${baseURL}?${queryString}` : baseURL;

                return (
                    urlWithQuery +
                    (releaseEnvShort === `prod`
                        ? ``
                        : (urlWithQuery.includes(`?`) ? `&` : `?`) + `env=${releaseEnvShort}`)
                );
            },

            /**
             * Email address to contact CANImmunize support at
             */
            contactSupport: `mailto://support@canimmunize.ca`,

            /**
             * Help page for a specific PHU
             */
            phuHelp: (phuId: string) =>
                `${protocol}://www.${hostname}/${langUrl}/report?phu=${phuId}`,
        },

        /**
         * External links to "Learn about CANImmunize" pages
         */
        learn: {
            knowledgeCentre: `${protocol}://learn.${hostname}/${langUrl}/knowledge-centre`,
        },

        /**
         * Sync sockets hook up to this URL
         */
        syncSocket: syncDomain,

        // prettier-ignore
        /**
         * Account/record creation/modification actions; and data sources,
         * including REST API routes for seeding Javelin with core data
         */
        syncServer: {
            /** Base data to hydrate Javelin **/
            'content':  {method: `get`, url: `${adminApiBaseUrl}/content` },
            'diseases': {method: `get`, url: `${adminApiBaseUrl}/diseases`},
            'vaccines': {method: `get`, url: `${adminApiBaseUrl}/vaccines`},
            'phu':      {method: `get`, url: `${adminApiBaseUrl}/phu`     },

            /** Sync record data **/
            'record-ids': {method: `get`,  url: `${syncAPI}/record-ids`},
            'sync':       {method: `post`, url: `${syncAPI}/sync`      },

            /** Login / Logout **/
            'login':     {method: `post`, url: `${syncAPI}/login`    },
            'login-mfa': {method: `post`, url: `${syncAPI}/login-mfa`},
            'logout':    {method: `get`,  url: `${syncAPI}/logout`   },

            /** Account settings **/
            'settings/update-account':  {method: `post`, url: `${settingsDomain}/update-account` },
            'settings/change-password': {method: `post`, url: `${settingsDomain}/change-password`},
            'settings/change-email':    {method: `post`, url: `${settingsDomain}/change-email`   },
            'change-phone':             {method: `post`, url: `${settingsDomain}/change-phone`   },

            /** Account creation & deletion **/
            'signup':                   {method: `post`, url: `${syncAPI}/signup`        },
            'delete/request':           {method: `post`, url: `${syncAPI}/delete/request`},
            'delete/cancel':            {method: `post`, url: `${syncAPI}/delete/cancel` },

            /** Determine if given email has an associated account **/
            'check-email-availability': {method: `post`, url: `${syncAPI}/check-email-availability`},

            /** Account unlock **/
            'unlock-account':              {method: `post`, url: `${syncAPI}/unlock-account`             },
            'resend-unlock-account-email': {method: `post`, url: `${syncAPI}/resend-unlock-account-email`},

            /** Account verification **/
            'confirm':             {method: `get`,  url: `${syncAPI}/confirm`                   },
            'verify-phone':        {method: `post`, url: `${settingsDomain}/verify-phone`       },
            'resend-verify-phone': {method: `get`,  url: `${settingsDomain}/resend-verify-phone`},
            'resend-verify-email': {method: `post`, url: `${syncAPI}/resend-verify-email`       },

            /** Password reset **/
            // Note: Duplicates are not a mistake - all 3 are handled by the same endpoint
            'forgot-password':         {method: `post`, url: `${syncAPI}/forgot-password`},
            'reset-password':          {method: `put`,  url: `${syncAPI}/forgot-password`},
            'get-reset-password-page': {method: `post`, url: `${syncAPI}/forgot-password`},
        }
    },

    /**
     * Links each notification status name to its associated color
     */
    notificationStatusConfig: [
        {name: `overdue`, color: `bg-mid-red`},
        {name: `approaching`, color: `bg-orange`},
        {name: `received`, color: `bg-mid-green`},
        {name: `future`, color: `bg-light-gray`},
        {name: `ignored`, color: `bg-light-gray`},
    ],

    /**
     * Diseases to show in disease tab container (in My Records area)
     */
    diseaseIDs: [`Measles`, `Mumps`, `Pertussis`, `Poliomyelitis`, `Rubella`],

    /**
     * Configure globally stored values
     */
    namespaces: {
        /**
         * Namespace for convenience access to data & libs when in dev (should be empty in prod)
         */
        dev: `__DEV__`,
        /**
         * Key/symbol storing polyglot on the window object
         */
        polyglot: configShared.i18n.i18nNamespace,
    },
};

/******************************************** EXPORTS *********************************************/
/**
 * Export configClient with configShared merged in
 */
export const configClient = deepMergeFn<typeof _configClient & typeof configShared>(
    _configClient as any,
    configShared as any,
    {clone: true}
);

export const i18nConfig = configClient.i18n;
export const appTitle = configClient.appTitle;
export const assetExts = configClient.assetExts;
export const loginData = configClient.appData.loginData;
export const windowNamespaces = configClient.namespaces;
export const devNS = configClient.namespaces.dev;
